import { useEffect } from 'react'

import { ModalActionTypes } from '@acorns/web-components'
import { useApolloErrorHandler, useRemoteStrings } from '@acorns/web-utils'

import {
  BankInterestRates,
  BankingProduct,
  useBankInterestRatesLazyQuery,
} from 'generated/types'
import { formatDateByDateObj } from 'utils/date/formatter'

// Force Date string to show date before the 'T' regardless of timezone
// more info: https://github.com/Acornsgrow/web-app/pull/8686#discussion_r1533064675
const overwriteToLocalTimeZone = (dateString: string) => {
  const date = new Date(dateString.split('T')[0])
  const userTimezoneOffset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() + userTimezoneOffset)
}

export const useMocApyRate = ({ isMoc }: { isMoc: boolean }) => {
  const [loadLazyQuery, { data, error, loading }] =
    useBankInterestRatesLazyQuery({
      variables: {
        productFilter: [
          BankingProduct.MightyOakChecking,
          BankingProduct.MightyOakEmergencyFund,
        ],
      },
    })

  useEffect(() => {
    if (isMoc) {
      loadLazyQuery()
    }
  }, [isMoc, loadLazyQuery])

  const bankInterestRates = (data?.bankInterestRates as BankInterestRates)
    ?.bankInterestRates

  const mocCheckingApy = bankInterestRates?.[0]?.currentApy || ''
  const mocEmergencyApy = bankInterestRates?.[1]?.currentApy || ''
  const mocLiveDate = bankInterestRates?.[0]?.liveDate || ''

  return {
    error,
    loading,
    mocCheckingApy,
    mocEmergencyApy,
    mocLiveDate: mocLiveDate
      ? formatDateByDateObj(overwriteToLocalTimeZone(mocLiveDate))
      : '',
  }
}

export const useMocApyRateWithErrorHandler = ({
  isMoc,
}: {
  isMoc: boolean
}) => {
  const { getRemoteString } = useRemoteStrings()

  useApolloErrorHandler('BankInterestRates', () => ({
    title: getRemoteString({ key: 'error.generic.title' }),
    body: getRemoteString({ key: 'error.generic.body' }),
    actions: [
      {
        label: getRemoteString({ key: 'error.generic.try_again' }),
        handler: () => {
          window.location.reload()
        },
        type: ModalActionTypes.primary,
      },
    ],
  }))

  return useMocApyRate({ isMoc })
}
